import { useEffect, useMemo, useState } from "react";
import { Outlet, useNavigate, useParams } from "react-router-dom";
import { AtSign, ChevronDown, Loader2, MessagesSquare, Paperclip, Plus, Search } from "lucide-react";
import { toast } from "sonner";
import { AppMainHeader } from "@/components/AppMainHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/contexts/AuthContext";
import { useChat, useNombreUsuario } from "@/contexts/ChatContext";
import { activarChatCliente, getApiErrorMessage } from "@/lib/api";
import { nombreVisible, coincideBusqueda, formatActividad } from "@/lib/clienteNombre";
import { ClienteLogo } from "@/components/clientes/ClienteLogo";
import { NuevoChatDialog } from "@/components/chat/NuevoChatDialog";
import { ChatTrabajosSwitch } from "@/components/chat/ChatTrabajosSwitch";
import { NuevoClienteDialog } from "@/components/clientes/NuevoClienteDialog";
import { MiembrosDialog } from "@/components/chat/MiembrosDialog";
import { cn } from "@/lib/utils";

export function ChatEmptyState() {
  return (
    <div className="h-full flex flex-col items-center justify-center gap-3 px-6 text-center">
      <MessagesSquare className="w-10 h-10 text-muted-foreground/40" strokeWidth={1.25} />
      <p className="text-sm text-muted-foreground max-w-xs">Elige un cliente para ver su conversación y sus trabajos pendientes.</p>
    </div>
  );
}

function PreviewMensaje({ mensaje, nombreDe, yo }) {
  if (!mensaje) return <span className="italic">Sin mensajes todavía</span>;
  if (mensaje.eliminado) return <span className="italic">Mensaje eliminado</span>;
  if (mensaje.tipo === "sistema") return <span className="italic">{mensaje.texto}</span>;
  const autor = mensaje.autor === yo ? "Tú" : nombreDe(mensaje.autor);
  let cuerpo = mensaje.texto;
  if (mensaje.tipo === "tarjeta_cliente") cuerpo = "Compartió los datos fiscales";
  else if (!cuerpo && mensaje.adjuntos?.length) {
    return (
      <span className="inline-flex items-center gap-1">
        {autor}: <Paperclip className="w-3 h-3" /> {mensaje.adjuntos[0].es_imagen ? "Foto" : mensaje.adjuntos[0].nombre}
      </span>
    );
  }
  return (
    <span>
      {autor}: {cuerpo}
    </span>
  );
}

function ChatItem({ chat, activo, onClick, nombreDe, yo }) {
  const { cliente, no_leidos: noLeidos, menciones, trabajos_abiertos: trabajos } = chat;
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "w-full flex items-center gap-3 px-3 py-3 rounded-lg my-0.5 text-left transition-colors",
        activo ? "bg-muted" : "hover:bg-muted/50",
        cliente.estado === "inactivo" && "opacity-60"
      )}
    >
      <ClienteLogo cliente={cliente} size={44} />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className={cn("truncate text-sm flex-1", noLeidos > 0 ? "font-semibold" : "font-medium")}>{nombreVisible(cliente)}</span>
          <span className={cn("text-[11px] shrink-0 tabular-nums", noLeidos > 0 ? "text-primary font-medium" : "text-muted-foreground")}>
            {formatActividad(chat.actividad)}
          </span>
        </div>
        <div className="flex items-center gap-2 mt-0.5">
          <span className={cn("truncate text-xs flex-1", noLeidos > 0 ? "text-foreground/80" : "text-muted-foreground")}>
            <PreviewMensaje mensaje={chat.ultimo_mensaje} nombreDe={nombreDe} yo={yo} />
          </span>
          {trabajos > 0 && noLeidos === 0 && (
            <span className="text-[10px] text-muted-foreground border rounded px-1 tabular-nums shrink-0" title="Trabajos abiertos">
              {trabajos}
            </span>
          )}
          {menciones > 0 && (
            <span className="h-[18px] w-[18px] rounded-full bg-primary text-primary-foreground flex items-center justify-center shrink-0" title="Te han mencionado">
              <AtSign className="w-3 h-3" />
            </span>
          )}
          {noLeidos > 0 && (
            <span className="min-w-[18px] h-[18px] px-1 rounded-full bg-destructive text-destructive-foreground text-[11px] font-medium flex items-center justify-center shrink-0 tabular-nums">
              {noLeidos}
            </span>
          )}
        </div>
      </div>
    </button>
  );
}

export default function ChatLayout() {
  const navigate = useNavigate();
  const { clienteId } = useParams();
  const { user, authLoading } = useAuth();
  const { chats, chatsLoaded, refreshChats } = useChat();
  const nombreDe = useNombreUsuario();
  const [q, setQ] = useState("");
  const [verInactivos, setVerInactivos] = useState(false);
  const [nuevoOpen, setNuevoOpen] = useState(false);
  const [crearClienteOpen, setCrearClienteOpen] = useState(false);
  const [clienteParaChat, setClienteParaChat] = useState(null);

  useEffect(() => {
    if (authLoading) return;
    if (!user) navigate("/login", { replace: true });
    else if (user.permissions?.chat === false) navigate("/candidatos", { replace: true });
  }, [authLoading, user, navigate]);

  const { activos, inactivos } = useMemo(() => {
    const lista = chats.filter((c) => coincideBusqueda(c.cliente, q));
    return {
      activos: lista.filter((c) => c.cliente.estado !== "inactivo"),
      inactivos: lista.filter((c) => c.cliente.estado === "inactivo"),
    };
  }, [chats, q]);

  const actual = chats.find((c) => c.cliente.id === clienteId);

  const crearChat = async (miembros) => {
    try {
      await activarChatCliente(clienteParaChat.id, miembros);
      await refreshChats();
      navigate(`/chat/${clienteParaChat.id}`);
      setClienteParaChat(null);
    } catch (err) {
      toast.error(getApiErrorMessage(err, "No se pudo crear el chat"));
      throw err;
    }
  };

  if (authLoading || !user || user.permissions?.chat === false) {
    return (
      <div className="min-h-dvh flex items-center justify-center bg-background">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="h-dvh flex flex-col bg-background overflow-hidden">
      <AppMainHeader title="Chat" titleSuffix={actual ? ` / ${nombreVisible(actual.cliente)}` : ""} titleAs="h5" />
      <div className="flex flex-1 min-h-0 overflow-hidden">
        <aside className={`${clienteId ? "hidden md:flex" : "flex"} w-full md:w-80 shrink-0 flex-col md:border-r border-gray-300 bg-background overflow-hidden`}>
          <div className="p-3 shrink-0 space-y-2.5 border-b border-gray-200">
          <ChatTrabajosSwitch activo="chat" />
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar cliente" className="pl-9 h-10" />
            </div>
            <Button size="icon" className="h-10 w-10 shrink-0" onClick={() => setNuevoOpen(true)} title="Nuevo chat">
              <Plus className="w-4 h-4" />
            </Button>
          </div>
          </div>
          <div className="flex-1 overflow-y-auto p-2 mobile-nav-scroll">
            {!chatsLoaded ? (
              <div className="flex justify-center py-12">
                <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
              </div>
            ) : chats.length === 0 ? (
              <div className="text-center px-6 py-12 space-y-3">
                <p className="text-sm text-muted-foreground">Todavía no estás en ningún chat de cliente.</p>
                <Button onClick={() => setNuevoOpen(true)} className="gap-2">
                  <Plus className="w-4 h-4" /> Crear el primero
                </Button>
              </div>
            ) : (
              <>
                <ul className="divide-y divide-border/70">
                  {activos.map((c) => (
                    <li key={c.cliente.id}>
                      <ChatItem chat={c} activo={c.cliente.id === clienteId} onClick={() => navigate(`/chat/${c.cliente.id}`)} nombreDe={nombreDe} yo={user.email} />
                    </li>
                  ))}
                </ul>
                {inactivos.length > 0 && (
                  <div className="mt-3">
                    <button type="button" onClick={() => setVerInactivos((v) => !v)} className="flex items-center gap-1.5 px-3 h-8 text-xs text-muted-foreground hover:text-foreground">
                      <ChevronDown className={cn("w-3.5 h-3.5 transition-transform", !verInactivos && "-rotate-90")} />
                      Inactivos ({inactivos.length})
                    </button>
                    {verInactivos && (
                      <ul className="divide-y divide-border/70">
                        {inactivos.map((c) => (
                          <li key={c.cliente.id}>
                            <ChatItem chat={c} activo={c.cliente.id === clienteId} onClick={() => navigate(`/chat/${c.cliente.id}`)} nombreDe={nombreDe} yo={user.email} />
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </aside>
        <div className={`${clienteId ? "flex" : "hidden md:flex"} flex-1 min-w-0 flex-col relative bg-muted/20`}>
          <div className="flex-1 min-h-0">
            <Outlet />
          </div>
        </div>
      </div>
      <NuevoChatDialog
        open={nuevoOpen}
        onOpenChange={setNuevoOpen}
        onElegir={(c) => {
          setNuevoOpen(false);
          setClienteParaChat(c);
        }}
        onCrearCliente={() => {
          setNuevoOpen(false);
          setCrearClienteOpen(true);
        }}
      />
      <NuevoClienteDialog open={crearClienteOpen} onOpenChange={setCrearClienteOpen} textoBoton="Siguiente" onCreated={(c) => setClienteParaChat(c)} />
      <MiembrosDialog
        open={!!clienteParaChat}
        onOpenChange={(o) => !o && setClienteParaChat(null)}
        titulo={clienteParaChat ? `Chat de ${nombreVisible(clienteParaChat)}` : ""}
        descripcion="¿Quién participa? Solo ellos verán la conversación."
        fijos={[user.email]}
        textoBoton="Crear chat"
        onConfirm={crearChat}
      />
    </div>
  );
}
